import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Check,
  FileText,
  Lightbulb,
  Rocket,
} from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import Seo from "@/components/Seo";
import { IDEAS } from "@/data/ideas";

const PACKAGE_ITEMS = [
  {
    icon: Lightbulb,
    title: "Erprobtes Konzept",
    copy: "Keine Luftschlösser: Jede Idee ist in der Praxis erprobt und für den Markt in Florida aufbereitet.",
  },
  {
    icon: FileText,
    title: "Fertiger Businessplan",
    copy: "Sie starten nicht bei null: Der Businessplan liegt bereits vor – auch passend für Visumsanträge.",
  },
  {
    icon: Rocket,
    title: "Gründungsbegleitung",
    copy: "Wir begleiten die Umsetzung Schritt für Schritt, bis Ihr Geschäft in Florida steht und läuft.",
  },
];

const FAQ = [
  {
    q: "Sind die Business-Ideen auf das E-2 Visum abgestimmt?",
    a: "Ja. Unsere Konzepte sind reale, aktiv zu führende Geschäfte – genau das, was das E-2 Visum verlangt. Wir stimmen die Idee mit Ihrer Visumsstrategie ab.",
  },
  {
    q: "Was kostet eine fixfertige Business-Idee?",
    a: "Das besprechen wir transparent im kostenlosen Erstgespräch – abgestimmt auf Ihr Vorhaben, bevor etwas entsteht.",
  },
  {
    q: "Kann ich eine Idee an meine Stärken anpassen?",
    a: "Ja. Die Konzepte sind erprobt, aber nicht in Stein gemeisselt: Wir passen sie an Ihren Standort in Florida und Ihre Stärken an.",
  },
];

export default function BusinessIdeen() {
  return (
    <>
      <Seo
        title="Fixfertige Business-Ideen für Florida – erprobt, mit Businessplan"
        description="Erprobte, fixfertige Geschäftsideen für Florida: Cleaning Service, E-Commerce, Hauspersonal-Vermittlung, Homewatch & Poolpflege – inklusive Businessplan und Gründungsbegleitung."
        keywords="Fixfertige Business-Ideen, Geschäftsidee Florida, Cleaning Service Florida gründen, E-Commerce USA starten, Homewatch Florida, Poolpflege-Service Florida, Hauspersonal Vermittlung Florida, E-2 Visum Geschäftsidee"
        jsonLd={[
          {
            "@type": "Service",
            name: "Fixfertige Business-Ideen für Florida",
            description:
              "Erprobte Geschäftskonzepte für den US-Markt inklusive Businessplan und Gründungsbegleitung – abgestimmt auf Visumsstrategien wie das E-2 Visum.",
            inLanguage: "de",
            provider: {
              "@type": "ProfessionalService",
              name: "Florida Business & Relocation",
            },
            areaServed: { "@type": "State", name: "Florida" },
          },
          {
            "@type": "FAQPage",
            mainEntity: FAQ.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]}
      />
      <Header />
      <main>
        <section
          data-testid="business-ideas-page"
          className="bg-sand pb-24 pt-36 lg:pt-44"
        >
          <div className="mx-auto max-w-3xl px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link
                to="/leistungen"
                data-testid="business-ideas-back-link"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-muted transition-colors hover:text-clay"
              >
                <ArrowLeft className="h-4 w-4" /> Leistungen
              </Link>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-10 text-xs uppercase tracking-[0.3em] text-clay"
              data-testid="business-ideas-eyebrow"
            >
              Fixfertige Business-Ideen
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 font-serif text-4xl leading-tight tracking-tight text-ink sm:text-5xl"
              data-testid="business-ideas-title"
            >
              Fixfertige Business-Ideen für Florida –{" "}
              <span className="italic text-clay">erprobt & gründungsbereit</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-8 border-l-2 border-clay pl-6 font-serif text-xl italic leading-relaxed text-ink-muted"
              data-testid="business-ideas-lead"
            >
              Sie wollen in Florida gründen, wissen aber noch nicht womit? Unsere
              Geschäftsideen sind bereits erprobt und komplett vorbereitet –
              inklusive Businessplan und Gründungsbegleitung.
            </motion.p>
          </div>

          <div className="mx-auto mt-20 max-w-5xl px-6">
            <ul
              className="grid grid-cols-1 gap-px border border-line bg-line md:grid-cols-2"
              data-testid="business-ideas-list"
            >
              {IDEAS.map((idea, index) => (
                <motion.li
                  key={idea.slug}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: index * 0.06 }}
                  className="group bg-sand transition-colors hover:bg-sand-deep"
                  data-testid={`business-idea-${index}`}
                >
                  <Link
                    to={`/business-ideen/${idea.slug}`}
                    data-testid={`business-idea-link-${idea.slug}`}
                    className="block h-full p-8 sm:p-10"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <idea.icon
                        className="h-6 w-6 text-clay"
                        strokeWidth={1.25}
                      />
                      <ArrowUpRight className="h-5 w-5 text-clay opacity-0 transition-opacity group-hover:opacity-100" />
                    </div>
                    <h2 className="mt-6 font-serif text-xl leading-snug tracking-tight text-ink transition-colors group-hover:text-clay sm:text-2xl">
                      {idea.title}
                    </h2>
                    <p className="mt-4 text-sm leading-relaxed text-ink-muted md:text-base">
                      {idea.short}
                    </p>
                  </Link>
                </motion.li>
              ))}
              <motion.li
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-col justify-center bg-clay p-8 sm:p-10"
                data-testid="business-ideas-cta-tile"
              >
                <p className="font-serif text-2xl italic leading-snug tracking-tight text-sand">
                  Welche Idee passt zu Ihnen?
                </p>
                <a
                  href="/kontakt"
                  data-testid="business-ideas-cta"
                  className="group mt-6 inline-flex w-fit items-center gap-3 border border-sand/60 px-6 py-3 text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-sand hover:text-clay"
                >
                  Im Erstgespräch herausfinden
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
              </motion.li>
            </ul>
          </div>

          <div className="mx-auto mt-24 max-w-3xl px-6">
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6 }}
              className="font-serif text-3xl tracking-tight text-ink sm:text-4xl"
              data-testid="business-ideas-package-title"
            >
              Das Komplettpaket:{" "}
              <span className="italic text-clay">Konzept, Businessplan, Begleitung</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-6 text-base leading-relaxed text-ink-muted"
            >
              Jede Idee kommt als Komplettpaket – Sie müssen nichts selbst
              entwickeln, sondern können direkt durchstarten.
            </motion.p>

            <ul className="mt-12 space-y-8" data-testid="business-ideas-package">
              {PACKAGE_ITEMS.map((item, i) => (
                <motion.li
                  key={item.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="flex items-start gap-5 border-b border-line pb-8"
                  data-testid={`package-item-${i}`}
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-line bg-sand-deep">
                    <item.icon className="h-5 w-5 text-clay" strokeWidth={1.25} />
                  </span>
                  <div>
                    <h3 className="font-serif text-xl tracking-tight text-ink">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink-muted md:text-base">
                      {item.copy}
                    </p>
                  </div>
                </motion.li>
              ))}
            </ul>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6 }}
              className="mt-20 border-l-2 border-clay bg-sand-deep p-8 sm:p-10"
              data-testid="business-ideas-visa-box"
            >
              <p className="font-serif text-xl italic tracking-tight text-ink sm:text-2xl">
                Passend zu Ihrem Visum
              </p>
              <p className="mt-4 text-sm leading-relaxed text-ink-muted md:text-base">
                Unsere Ideen sind so gewählt, dass sie zu Ihrer Visumsstrategie
                passen – etwa zum{" "}
                <Link
                  to="/visa/e-2-visum"
                  data-testid="business-ideas-e2-link"
                  className="text-clay underline decoration-clay/40 underline-offset-4 transition-colors hover:decoration-clay"
                >
                  E-2 Visum
                </Link>
                , das ein reales, aktiv geführtes Unternehmen verlangt. So werden
                aus Geschäftsidee und Visum ein stimmiger Plan.
              </p>
            </motion.div>

            <motion.section
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6 }}
              className="mt-20"
              data-testid="business-ideas-faq"
            >
              <h2 className="font-serif text-2xl tracking-tight text-ink sm:text-3xl">
                Häufige Fragen zu den Business-Ideen
              </h2>
              <div className="mt-8 space-y-10">
                {FAQ.map((f, i) => (
                  <div key={i} data-testid={`business-ideas-faq-item-${i}`}>
                    <h3 className="flex items-start gap-3 font-serif text-lg tracking-tight text-ink sm:text-xl">
                      <Check className="mt-1 h-4 w-4 shrink-0 text-clay" />
                      {f.q}
                    </h3>
                    <p className="mt-3 pl-7 text-sm leading-relaxed text-ink-muted md:text-base">
                      {f.a}
                    </p>
                  </div>
                ))}
              </div>
            </motion.section>

            <div
              className="mt-20 border border-line bg-sand-deep p-10"
              data-testid="business-ideas-cta-box"
            >
              <p className="font-serif text-2xl italic leading-snug tracking-tight text-ink">
                Starten wir Ihre Idee gemeinsam?
              </p>
              <p className="mt-4 text-sm leading-relaxed text-ink-muted md:text-base">
                Im kostenlosen Erstgespräch zeigen wir Ihnen die Konzepte im
                Detail – und prüfen, welche Idee zu Ihnen und Ihrem Visum passt.
              </p>
              <a
                href="/kontakt"
                data-testid="business-ideas-cta-button"
                className="group mt-8 inline-flex items-center gap-3 bg-clay px-8 py-4 text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-clay-dark"
              >
                Kostenloses Erstgespräch buchen
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
